// React Dependencies
import React, { useState } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { TextareaAutosize, Dialog, DialogContent, DialogActions } from '@material-ui/core';
import { toast } from 'react-toastify';

// Internal Dependencies
import ApiService from '../../../../../services/apiService';
import withLoyaltyConnect from '../WithLoyaltyConnect';
import OverLoading from '../../../../../components/Loading';
import MButton from '../../../../../components/MButton';
import MText from '../../../../../components/Common/MText';
import RadioBtnSelect from '../../../../../components/Common/RadioBtnSelect';
import LoyaltyMenuDescriptionBox from '../../../../../components/LoyaltyMenuDescriptionBox';
import FloatingColourPicker from '../../../../../components/FloatingColourPicker';
import Colours from '../../../../../styles/colours';
import AvailableImage from './AvailableImage';
import Photo, { maxFileSize } from '../../Photo';
import { Status } from '../../../../../common/constants';
import { Trim, validateImageWidth } from '../../../../../common/util';
import { IsEmpty } from '../../../../../common/checks';
import { AvailableImages, DesignTypeOptions } from '../constants';

const stampsImage = Array.from({ length: 10 }, (_, i) => `/images/stamp-holders/stamp-${i + 1}.png`);
const maxLcNameLength = 35;

const Design = (props) => {
  const { loyaltyCardId, pageId } = props.match.params;

  const isEditable = props.loyaltyProgram?.status === Status.Draft || props.loyaltyProgram?.status === Status.Live;
  const designType = props?.loyaltyProgram?.design?.designType;

  const [loading, setLoading] = useState(false);
  const [isMaxCharactersReached, setIsMaxCharactersReached] = useState(false);
  const [isOpenViewTemplateModal, setIsOpenViewTemplateModal] = useState(false);
  const [stampsHolderColourPickerOpen, setStampsHolderColourPickerOpen] = useState(false);

  const stampsHolderBackgroundColour = IsEmpty(props.loyaltyProgram?.design?.stampsHolderBackgroundColour)
    ? Colours.Gray7
    : props.loyaltyProgram?.design?.stampsHolderBackgroundColour;

  const handleImage = async (imageObj) => {
    const formData = new FormData();
    formData.append('image', imageObj.file);
    try {
      setLoading(true);
      const isValidated = await validateImageWidth(imageObj.data, { min: 900 });
      if (isValidated) {
        const mediaUrl = await ApiService.uploadLoyaltyCardMedia(pageId, loyaltyCardId, formData);
        props.updateLoyaltyCardAction({
          image:  mediaUrl,
          design: {
            ...props.loyaltyProgram.design,
            designType:     DesignTypeOptions.Manual,
            uploadedDesign: mediaUrl,
          },
        });

        toast.success('Loyalty card image has been updated');
      } else {
        toast.error(`The image width should not be less than 900 pixels`);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const onImageTemplateClick = (data) => {
    props.updateLoyaltyCardAction({
      design: {
        ...props.loyaltyProgram.design,
        designType:                   DesignTypeOptions.Template,
        selectedLoyaltyCardTemplate:  data.top,
        selectedStampsHolderTemplate: data.btm,
        templatePreview:              data.image,
      },
    });
  };

  const onChangeDesign = (type) => {
    props.updateLoyaltyCardAction({
      design: {
        ...props.loyaltyProgram.design,
        designType: type,
      },
    });
  };

  const onStampsHolderColourPicked = (value) => {
    props.updateLoyaltyCardAction({
      design: {
        ...props.loyaltyProgram.design,
        designType:                   DesignTypeOptions.Manual,
        stampsHolderBackgroundColour: value,
      },
    });
  };

  const handleNameChange = (e) => {
    const name = Trim(e.target.value);
    if (name.length >= maxLcNameLength) {
      setIsMaxCharactersReached(true);
      props.updateLoyaltyCardAction({ name: name.slice(0, maxLcNameLength) });
    } else {
      setIsMaxCharactersReached(false);
      props.updateLoyaltyCardAction({ name: e.target.value });
    }
  };

  return (
    <>
      {loading && <OverLoading />}
      <div className="mt-4">
        <LoyaltyMenuDescriptionBox
          text="Customise the design of the loyalty card by uploading a background visual or selecting a visual from the library. View the card template to find out size requirements and visual area restrictions. The logo on the loyalty card will be taken from the brand's home page logo."
        />
        <MText
          className="mt-4"
          size={13}
          colour={Colours.Black}
          style={{ fontWeight: 500 }}
          text="Loyalty Card Name"
        />
        <MText
          className="mb-2"
          size={12}
          colour={Colours.Gray}
          text={`(Max. ${maxLcNameLength} characters)`}
        />
        <TextareaAutosize
          disabled={!isEditable}
          className="w-100 p-2"
          maxRows={2}
          value={props.loyaltyProgram?.name}
          onChange={(e) => handleNameChange(e)}
          style={{
            resize:       'none',
            background:   'transparent',
            border:       `1px solid ${Colours.Gray5}`,
            borderRadius: 10,
            fontSize:     12,
          }}
          onKeyDown={(e) => {
            const text = e.target.value;
            const rows = text.split('\n').length;
            if (rows >= 2 && e.key === 'Enter') {
              e.preventDefault(); // Prevent the default behavior if the input already has 2 lines of text
            }
          }}
        />

        {isMaxCharactersReached && (
          <MText
            className="ml-2"
            size={12}
            colour={Colours.Orange}
            text={`Max. ${maxLcNameLength} characters reached`}
          />
        )}
        <MText
          className="mt-4"
          size={13}
          colour={Colours.Black}
          text="Loyalty Card Image"
          style={{ fontWeight: 500 }}
        />
        <div className="ml-2 mt-3">
          <div className="d-flex flex-row">
            <RadioBtnSelect
              options={[{ label: `Upload main card image (max ${maxFileSize}MB)`, value: true }]}
              value={designType === DesignTypeOptions.Manual}
              onChange={() => onChangeDesign(DesignTypeOptions.Manual)}
              disabled={!isEditable}
              radioWrapperStyle={{ alignItems: 'flex-start' }}
              radioStyle={{ margin: '0px 9px', padding: 0 }}
            />
            <MButton
              textSize={13}
              variant="text"
              colour={Colours.Transparent}
              style={{ marginTop: -8, marginLeft: -50 }}
              onClick={() => setIsOpenViewTemplateModal(true)}
              textColour={Colours.LightBlue}
              text="(view template)"
            />
          </div>
          <div
            className="w-100 mb-4"
            style={{ display: (designType === DesignTypeOptions.Manual ? 'block' : 'none') }}
          >
            <div style={{ width: 280 }}>
              <Photo
                title="+ Click to upload image"
                image={props.loyaltyProgram?.design?.uploadedDesign}
                disabled={!isEditable}
                handleImage={handleImage}
              />
            </div>
            {
              !IsEmpty(props.loyaltyProgram?.design?.uploadedDesign) && (
                <>
                  <div
                    className="mt-2 py-4 px-3"
                    style={{
                      borderRadius:    12,
                      width:           280,
                      backgroundColor: stampsHolderBackgroundColour,
                    }}
                  >
                    <div
                      style={{
                        display:             'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        justifyItems:        'center',
                        alignItems:          'center',
                        gap:                 15,
                      }}
                    >
                      {stampsImage.map((row) => (
                        <img key={row} src={row} alt={row} style={{ width: 60, height: 60, objectFit: 'contain' }} />
                      ))}
                      <img
                        alt="right-arrow"
                        src="/images/stamp-holders/white-right-arrow.png"
                        style={{ width: 60, height: 60, objectFit: 'contain' }}
                      />
                      <MButton
                        disabled
                        variant="contained"
                        style={{
                          backgroundColor: `white`,
                          borderRadius:    99,
                          width:           40,
                          height:          30,
                        }}
                      />
                    </div>
                  </div>
                  <div className="d-flex flex-row flex-wrap mt-3">
                    <FloatingColourPicker
                      open={stampsHolderColourPickerOpen}
                      placement="bottom"
                      colour={props.loyaltyProgram?.design?.stampsHolderBackgroundColour}
                      onClose={() => setStampsHolderColourPickerOpen(false)}
                      onChange={onStampsHolderColourPicked}
                    >
                      <div>
                        <MButton
                          disabled={!isEditable}
                          onClick={() => setStampsHolderColourPickerOpen(!stampsHolderColourPickerOpen)}
                          colour={stampsHolderBackgroundColour}
                          style={{
                            borderRadius: 0,
                            height:       28,
                            cursor:       'pointer',
                          }}
                        />
                      </div>
                    </FloatingColourPicker>
                    <MText text="Click to select colour of the stamp card" size={13} className="ml-2 mt-1" />
                  </div>
                </>
              )
            }

          </div>
        </div>

        <div className="ml-2 mt-3">
          <RadioBtnSelect
            options={[{ label: 'Choose from available images', value: true }]}
            value={designType === DesignTypeOptions.Template}
            onChange={() => onChangeDesign(DesignTypeOptions.Template)}
            disabled={!isEditable}
            radioWrapperStyle={{ alignItems: 'flex-start' }}
            radioStyle={{ margin: '0px 9px', padding: 0 }}
          />
          <div
            className="w-100 mt-3"
            style={{ display: (designType === DesignTypeOptions.Template ? 'block' : 'none') }}
          >
            <div className="d-flex flex-row flex-wrap" style={{ gap: 12 }}>
              {
                AvailableImages.map((data) => (
                  <AvailableImage
                    key={data.image}
                    src={data.image}
                    selectedAvailableImage={props.loyaltyProgram?.design?.templatePreview}
                    onClick={() => onImageTemplateClick(data)}
                  />
                ))
              }
            </div>
          </div>
        </div>

        <Dialog open={isOpenViewTemplateModal}>
          <DialogContent style={{ paddingTop: 32, paddingLeft: 32, paddingRight: 32 }}>
            <img src="/images/loyalty-card-template-R.png" alt="" />
          </DialogContent>
          <DialogActions className="mt-2 mb-3">
            <MButton
              onClick={() => setIsOpenViewTemplateModal(false)}
              textSize={15}
              className="float-right px-3 mr-4"
              variant="outlined"
              text="Close"
            />
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

Design.propTypes = {
  match:          PropTypes.shape(Object).isRequired,
  loyaltyProgram: PropTypes.shape({
    id:     PropTypes.string,
    pageId: PropTypes.string,
    name:   PropTypes.string,
    image:  PropTypes.string,
    status: PropTypes.oneOf([Status.Draft, Status.Live, Status.Deactivated]),
    design: PropTypes.shape({
      designType:                   PropTypes.oneOf(Object.values(DesignTypeOptions)),
      uploadedDesign:               PropTypes.string,
      selectedLoyaltyCardTemplate:  PropTypes.string,
      selectedStampsHolderTemplate: PropTypes.string,
      stampsHolderBackgroundColour: PropTypes.string,
      templatePreview:              PropTypes.string,
    }),
  }).isRequired,
  updateLoyaltyCardAction: PropTypes.func.isRequired,
};

export default withLoyaltyConnect(Design);
